import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { api } from '../contexts/ApiContext';

type HealthLevel = 'unknown' | 'ok' | 'warn' | 'error';

const POLL_MS = 60_000;
/** Short enough for UX; slow API responses may take longer — user can retry via click. */
const HEALTH_TIMEOUT_MS = 25_000;

function normalizeHealthLevel(res: {
  status: number;
  data: unknown;
}): HealthLevel {
  if (res.status !== 200) return 'error';
  const data = res.data as { status?: string; issues?: unknown[] };
  const st = String(data?.status || '').toLowerCase();
  const issues = Array.isArray(data?.issues) ? data.issues : [];
  if (st === 'degraded') return 'warn';
  if (st === 'healthy') return issues.length > 0 ? 'warn' : 'ok';
  return 'warn';
}

async function fetchHealthLevel(): Promise<HealthLevel> {
  try {
    const res = await api.get('/api/health', { timeout: HEALTH_TIMEOUT_MS });
    return normalizeHealthLevel({ status: res.status, data: res.data });
  } catch {
    return 'error';
  }
}

function levelColors(level: HealthLevel): { bg: string; label: string } {
  switch (level) {
    case 'ok':
      return {
        bg: 'rgba(46, 125, 50, 0.12)',
        label: 'Online'
      };
    case 'warn':
      return {
        bg: 'rgba(237, 108, 2, 0.14)',
        label: 'Degraded'
      };
    case 'error':
      return {
        bg: 'rgba(211, 47, 47, 0.12)',
        label: 'Offline'
      };
    default:
      return {
        bg: 'action.hover',
        label: 'Checking…'
      };
  }
}

const ICON_SX = { fontSize: 18, flexShrink: 0, display: 'block' } as const;

function HealthStateIcon({ level }: { level: HealthLevel }) {
  switch (level) {
    case 'ok':
      return <CheckCircleIcon sx={{ ...ICON_SX, color: 'success.main' }} />;
    case 'warn':
      return <WarningAmberIcon sx={{ ...ICON_SX, color: 'warning.main' }} />;
    case 'error':
      return <ErrorOutlineIcon sx={{ ...ICON_SX, color: 'error.main' }} />;
    default:
      return <HourglassEmptyIcon sx={{ ...ICON_SX, color: 'text.secondary' }} />;
  }
}

export function ServerHealthPill() {
  const [level, setLevel] = useState<HealthLevel>('unknown');
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const reqSeq = useRef(0);

  const runCheck = useCallback(async () => {
    const id = ++reqSeq.current;
    setChecking(true);
    try {
      const next = await fetchHealthLevel();
      if (reqSeq.current !== id) return;
      setLevel(next);
      setLastChecked(new Date());
    } finally {
      if (reqSeq.current === id) setChecking(false);
    }
  }, []);

  useEffect(() => {
    void runCheck();
    const t = window.setInterval(() => void runCheck(), POLL_MS);
    return () => window.clearInterval(t);
  }, [runCheck]);

  const colors = levelColors(level);
  const tooltipLines = [
    'GET /api/health — click to run now',
    lastChecked ? `Last checked: ${lastChecked.toLocaleTimeString()}` : 'Last checked: —',
    checking ? 'Status: checking…' : `Status: ${colors.label}`
  ];

  return (
    <Tooltip
      title={
        <Box sx={{ py: 0.25 }}>
          {tooltipLines.map((line, i) => (
            <Typography key={i} variant="caption" component="div" display="block">
              {line}
            </Typography>
          ))}
        </Box>
      }
      placement="bottom-end"
    >
      <Chip
        component="button"
        type="button"
        size="small"
        onClick={() => void runCheck()}
        aria-label={`Server health: ${checking ? 'checking' : colors.label}. Click to refresh.`}
        sx={{
          height: 28,
          cursor: checking ? 'wait' : 'pointer',
          bgcolor: colors.bg,
          border: '1px solid',
          borderColor: 'divider',
          opacity: checking ? 0.92 : 1,
          transition: 'opacity 0.15s',
          '&:hover': {
            bgcolor: colors.bg,
            filter: 'brightness(0.97)'
          },
          '& .MuiChip-label': { px: 1, py: 0, color: 'text.primary' }
        }}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <HealthStateIcon level={level} />
            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
              API
            </Typography>
          </Box>
        }
      />
    </Tooltip>
  );
}
